import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Check, CreditCard, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PLAN_DETAILS = {
  professional: {
    name: "Professional",
    monthlyPrice: 25,
    annualPrice: 20,
    credits: 25,
    description: "For music supervisors, producers, indie labels",
  },
  pro_plus: {
    name: "Pro+",
    monthlyPrice: 75,
    annualPrice: 60,
    credits: 100,
    description: "For agencies, sync teams, catalogue managers",
  },
  business: {
    name: "Business",
    monthlyPrice: 250,
    annualPrice: 200,
    credits: 500,
    description: "For brands, production companies, mid-size platforms",
  },
};

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [processing, setProcessing] = useState(false);

  const planId = searchParams.get("plan");
  const billing = searchParams.get("billing") || "monthly";
  const plan = PLAN_DETAILS[planId];

  useEffect(() => {
    if (!plan) {
      navigate("/pricing");
    }
  }, [plan, navigate]);

  if (!plan) return null;

  const price = billing === "annual" ? plan.annualPrice : plan.monthlyPrice;
  const annualTotal = billing === "annual" ? plan.annualPrice * 12 : null;
  const savings = billing === "annual" ? (plan.monthlyPrice - plan.annualPrice) * 12 : 0;

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      toast.info("Please sign in or create an account first");
      navigate(`/register?redirect=/checkout?plan=${planId}&billing=${billing}`);
      return;
    }

    setProcessing(true);

    // Stripe integration placeholder
    // When Stripe is configured, this will call:
    // POST /api/stripe/create-checkout-session { plan_id, billing_period }
    // and redirect to the Stripe hosted checkout page
    
    setTimeout(() => {
      setProcessing(false);
      toast.info("Stripe checkout is being configured. Your subscription will be activated shortly.");
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-syncchain-bg text-syncchain-text" data-testid="checkout-page">
      {/* Nav */}
      <nav className="border-b border-white/5 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_87e61a64-90d2-4c20-8ba6-31392dc5e379/artifacts/xzzkl8ic_SyncChain_White.png"
              alt="SyncChain"
              className="h-8"
            />
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/pricing")}
            className="text-syncchain-text-secondary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Pricing
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          
          {/* Order Summary */}
          <div className="lg:col-span-3">
            <h1 className="text-2xl font-bold mb-6">Complete Your Subscription</h1>

            <Card className="bg-syncchain-paper border-white/10">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg text-syncchain-text flex items-center justify-between">
                  Order Summary
                  <Badge variant="outline" className="text-syncchain-accent border-syncchain-accent/30">
                    {billing === "annual" ? "Annual" : "Monthly"}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-white/5">
                  <div>
                    <p className="font-semibold text-syncchain-text">{plan.name} Plan</p>
                    <p className="text-xs text-syncchain-text-muted mt-0.5">{plan.description}</p>
                  </div>
                  <p className="text-lg font-bold text-syncchain-text">${price}<span className="text-sm font-normal text-syncchain-text-muted">/mo</span></p>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    <span className="text-syncchain-text-secondary">{plan.credits} credits per month</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    <span className="text-syncchain-text-secondary">Full rights resolution per credit</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    <span className="text-syncchain-text-secondary">Cancel anytime</span>
                  </div>
                </div>

                {billing === "annual" && (
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 mt-4">
                    <p className="text-xs text-emerald-400 font-medium">
                      You save ${savings}/year with annual billing (${annualTotal}/yr billed annually)
                    </p>
                  </div>
                )}

                <div className="border-t border-white/10 pt-4 mt-4">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-syncchain-text">
                      {billing === "annual" ? "Billed annually" : "Due today"}
                    </span>
                    <span className="text-xl font-bold text-syncchain-accent">
                      ${billing === "annual" ? annualTotal : price}
                      <span className="text-xs font-normal text-syncchain-text-muted">
                        {billing === "annual" ? "/yr" : "/mo"}
                      </span>
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Billing Toggle */}
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => navigate(`/checkout?plan=${planId}&billing=monthly`)}
                className={`flex-1 p-3 rounded-lg border text-center text-sm transition-all ${
                  billing === "monthly"
                    ? "border-syncchain-accent bg-syncchain-accent/10 text-syncchain-text"
                    : "border-white/10 text-syncchain-text-muted hover:border-white/20"
                }`}
              >
                Monthly — ${plan.monthlyPrice}/mo
              </button>
              <button
                onClick={() => navigate(`/checkout?plan=${planId}&billing=annual`)}
                className={`flex-1 p-3 rounded-lg border text-center text-sm transition-all ${
                  billing === "annual"
                    ? "border-emerald-500 bg-emerald-500/10 text-syncchain-text"
                    : "border-white/10 text-syncchain-text-muted hover:border-white/20"
                }`}
              >
                Annual — ${plan.annualPrice}/mo
                <span className="ml-1 text-xs text-emerald-400">Save 20%</span>
              </button>
            </div>
          </div>

          {/* Payment */}
          <div className="lg:col-span-2">
            <Card className="bg-syncchain-paper border-white/10 sticky top-8">
              <CardHeader className="pb-4">
                <CardTitle className="text-base text-syncchain-text flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-syncchain-accent" />
                  Payment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!isAuthenticated && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-4">
                    <p className="text-xs text-amber-400">
                      You'll need to sign in or create an account before completing payment.
                    </p>
                  </div>
                )}

                {isAuthenticated && (
                  <div className="bg-white/5 rounded-lg p-3 mb-2">
                    <p className="text-xs text-syncchain-text-muted">Signed in as</p>
                    <p className="text-sm text-syncchain-text font-medium">{user?.email}</p>
                  </div>
                )}

                {/* Stripe placeholder area */}
                <div className="border border-dashed border-white/20 rounded-lg p-6 text-center">
                  <Lock className="w-8 h-8 text-syncchain-text-muted mx-auto mb-2" />
                  <p className="text-xs text-syncchain-text-muted">
                    Secure payment powered by Stripe
                  </p>
                  <p className="text-[10px] text-syncchain-text-muted mt-1">
                    Card details will be collected securely via Stripe Checkout
                  </p>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={processing}
                  className="w-full h-12 bg-syncchain-accent hover:bg-syncchain-accent-hover text-white font-semibold rounded-full"
                  data-testid="checkout-pay-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      {isAuthenticated ? "Subscribe Now" : "Sign In & Subscribe"}
                    </>
                  )}
                </Button>

                <p className="text-[10px] text-syncchain-text-muted text-center leading-relaxed">
                  By subscribing, you agree to our Terms of Service. You can cancel your subscription at any time from your account settings.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
